import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "utils/api";
import { showMessage } from "./common.slice";
import { getSessionData } from "utils/common";

const initialState = {
  orderBookList: [],
  orderBookPendingLoading: false,
  totalRecordOrderBook: 0,
  currentPageOrderBook: 1,
  searchTextOrderBook: "",
  isCompletedOrderType: "pending",
  isOrderBookUpdated: false,
};

export const getOrderBookList = createAsyncThunk(
  "orderbook/get",
  async (params = {}, { rejectWithValue }) => {
    try {
      const session = getSessionData() || {};
      const userId = params.userId || session.userId || session.id || null;
      const roles = session.roles || [session.roleName || "User"];
      const isUserRole = String(roles?.[0] || "").toLowerCase() === "user";
      const isCompleted = params.isCompleted === "completed";

      const payload = {
        userId,
        pageNo: params.pageNo || 1,
        pageSize: params.pageSize || 50,
        segmentId: params.segment?.value ?? null,
        clientId: params.client?.value ?? (isUserRole ? userId : null),
        symbol: params.symbol?.value ?? null,
        searchText: params.searchText || null,
      };

      // History Module Step 6 — pending vs final orders use dedicated SPs
      const endpoint = isCompleted ? "orders/get-history" : "orders/get-pending";
      if (isCompleted) {
        const today = new Date();
        const ymd = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        // Completed tab: last 30 days by default (no date filters on Order Book UI yet)
        const start = new Date(today);
        start.setDate(start.getDate() - 30);
        payload.startDate = ymd(start);
        payload.endDate = ymd(today);
      }

      const { data } = await api.post(endpoint, payload);
      if (data?.status === 200) {
        const records = (data.data?.records || []).map((row) => ({
          ...row,
          segment: row.segment || row.Segment || "",
          segmentId: row.segmentId ?? row.SegmentId ?? null,
          clientName: row.clientName || row.ClientName || "",
          script: row.script || row.displayName || row.symbol || "",
          displayName: row.displayName || row.script || row.symbol || "",
          orderTypeName: row.orderTypeName || "",
          executionTypeName: row.executionTypeName || "",
          status: row.status || row.stateName || (isCompleted ? "Completed" : "Pending"),
          addTime: row.timeSetupText || row.addTime || "",
          tradeDateTime: row.timeDoneText || row.timeSetupText || row.tradeDateTime || "",
        }));
        return {
          data: { ...data.data, records },
          appendData: !!params.appendData,
        };
      }
      return rejectWithValue(data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: error.message });
    }
  }
);

export const deleteOrder = createAsyncThunk(
  "orderbook/delete",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const ids = Array.isArray(id) ? id.map((x) => x.id).filter(Boolean) : [id];
      const { data } = await api.delete("order/delete-order", { data: ids });
      if (data?.status === 200) {
        const successMessage = ids.length > 1
          ? "Orders cancelled successfully."
          : "Order cancelled successfully.";
        dispatch(showMessage({ message: successMessage, varient: "success" }));
        return data;
      }
      return rejectWithValue(data);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Unable to cancel order";
      dispatch(showMessage({ message }));
      return rejectWithValue(error?.response?.data || { message });
    }
  }
);

export const editOrder = createAsyncThunk(
  "orderbook/edit",
  async ({ order, price }, { dispatch, rejectWithValue }) => {
    try {
      const session = getSessionData() || {};
      const userIp = session.userIpFinal || session.userIp || "";
      const payload = {
        userId: order.userId,
        id: order.id,
        deviceType: 1,
        segmentId: order.segmentId,
        script: order.script,
        displayName: order.displayName,
        userIp,
        orderType: order.orderType,
        lot: order.lot,
        qty: order.qty,
        rate: Number(price),
        executionType: order.executionType,
        symbol: order.symbol,
      };
      const { data } = await api.post("order/save-order", payload);
      if (data?.status === 200) {
        dispatch(showMessage({ message: data.message || "Order updated", varient: "success" }));
        return data;
      }
      return rejectWithValue(data);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Unable to update order";
      dispatch(showMessage({ message }));
      return rejectWithValue(error?.response?.data || { message });
    }
  }
);

export const executeOrder = createAsyncThunk(
  "orderbook/execute",
  async ({ id, displayName, segmentId }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.post("trade/convert-to-trade-using-order", null, {
        params: {
          orderId: id,
          DisplayName: displayName,
          SegmentId: segmentId,
        },
      });
      if (data?.status === 200) {
        dispatch(showMessage({ message: data.message || "Order executed", varient: "success" }));
        return data;
      }
      return rejectWithValue(data);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Unable to execute order";
      dispatch(showMessage({ message }));
      return rejectWithValue(error?.response?.data || { message });
    }
  }
);

const slice = createSlice({
  name: "orderbook",
  initialState,
  reducers: {
    setOrderBookSearchText: (state, action) => {
      state.searchTextOrderBook = action.payload;
    },
    setOrderBookPage: (state, action) => {
      state.currentPageOrderBook = action.payload;
    },
    setOrderBookStatus: (state, action) => {
      state.isCompletedOrderType = action.payload;
    },
    setOrderBookList: (state, action) => {
      state.orderBookList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderBookList.pending, (state) => {
        state.orderBookPendingLoading = true;
      })
      .addCase(getOrderBookList.rejected, (state) => {
        state.orderBookPendingLoading = false;
      })
      .addCase(getOrderBookList.fulfilled, (state, action) => {
        const records = action.payload?.data?.records || [];
        state.orderBookList = action.payload.appendData
          ? [...state.orderBookList, ...records]
          : records;
        state.totalRecordOrderBook = action.payload?.data?.totalRecords || 0;
        state.orderBookPendingLoading = false;
        state.isOrderBookUpdated = false;
      })
      .addCase(deleteOrder.pending, (state) => {
        state.isOrderBookUpdated = false;
      })
      .addCase(deleteOrder.fulfilled, (state) => {
        state.isOrderBookUpdated = true;
      })
      .addCase(deleteOrder.rejected, (state) => {
        state.isOrderBookUpdated = false;
      })
      .addCase(editOrder.pending, (state) => {
        state.isOrderBookUpdated = false;
      })
      .addCase(editOrder.fulfilled, (state) => {
        state.isOrderBookUpdated = true;
      })
      .addCase(editOrder.rejected, (state) => {
        state.isOrderBookUpdated = false;
      })
      .addCase(executeOrder.pending, (state) => {
        state.isOrderBookUpdated = false;
      })
      .addCase(executeOrder.fulfilled, (state) => {
        state.isOrderBookUpdated = true;
      })
      .addCase(executeOrder.rejected, (state) => {
        state.isOrderBookUpdated = false;
      });
  },
});

export const { setOrderBookSearchText, setOrderBookPage, setOrderBookStatus, setOrderBookList } =
  slice.actions;

export default slice.reducer;
